import React, { useEffect, useRef } from 'react';
import { ValidationIndicator } from './ValidationIndicator';
import { devLog } from '../utils/devLogger';
import { loadMSICCodes, getMSICCodes, isMSICLoaded } from '../services/msicService';

interface BusinessTableProps {
  field: any;
  companyInfo: any;
  formData: Record<string, any>;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  hasFieldError?: (fieldName: string, contextRef?: string) => boolean;
}

const formatDateForContext = (dateStr: string): string => {
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  return dateStr.replace(/-/g, '');
};

const getPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.currentPeriod || companyInfo.reporting_period;
  if (!period) {
    return `context_${member}`;
  }

  const formattedStart = formatDateForContext(period.start);
  const formattedEnd = formatDateForContext(period.end);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  if (item.properties?.period_type === 'duration') {
    return `fromto_${formattedStart}_${formattedEnd}${memberSuffix}`;
  }
  return `asof_${formattedEnd}${memberSuffix}`;
};

const getMemberLabel = (index: number) => {
  if (index === 0) return 'One';
  if (index === 1) return 'Two';
  return 'Three';
};

/**
 * BusinessTable - Handles DetailsOfPrincipalActivitiesOfBusinessAbstract section
 * Modal open/close and MSIC list stay in the DOM so the 800+ field form does not re-render.
 */
const BusinessTableComponent: React.FC<BusinessTableProps> = ({
  field,
  companyInfo,
  formData,
  onFieldChange,
  hasFieldError
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const retryBtnRef = useRef<HTMLButtonElement>(null);
  const activeMemberIndexRef = useRef(0);
  const listBuiltRef = useRef(false);

  const table = field.structure?.children?.[0];
  const axis = table?.structure?.children?.[0];
  const members = axis?.structure?.children?.[0]?.structure?.children || [];
  const lineItems = field.structure?.children?.[1]?.structure?.children || [];

  const getBusinessContext = (item: any, member: any) => {
    return getPeriodContext(item, companyInfo, `_${member.name}`);
  };

  const setStatus = (mode: 'loading' | 'error' | 'empty' | 'list', message?: string) => {
    if (statusRef.current) {
      statusRef.current.style.display = mode === 'list' ? 'none' : 'block';
      const text = statusRef.current.querySelector('p');
      if (text) text.textContent = message || '';
    }
    if (tableWrapRef.current) {
      tableWrapRef.current.style.display = mode === 'list' ? 'block' : 'none';
    }
    if (retryBtnRef.current) {
      retryBtnRef.current.style.display = mode === 'error' ? 'inline-flex' : 'none';
    }
  };

  const filterRows = (term: string) => {
    const q = term.trim().toLowerCase();
    const rows = tbodyRef.current?.querySelectorAll('tr') || [];
    let visible = 0;
    rows.forEach((tr) => {
      const code = tr.dataset.code || '';
      const description = tr.dataset.description || '';
      const match = !q || code.includes(q) || description.includes(q);
      tr.style.display = match ? '' : 'none';
      if (match) visible += 1;
    });

    if (!listBuiltRef.current) return;
    if (visible === 0) {
      setStatus('empty', 'No MSIC codes found');
    } else {
      setStatus('list');
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.classList.remove('open');
      modalRef.current.style.display = 'none';
    }
    if (searchInputRef.current) searchInputRef.current.value = '';
    filterRows('');
  };

  const openModal = (memberIndex: number) => {
    activeMemberIndexRef.current = memberIndex;
    if (searchInputRef.current) searchInputRef.current.value = '';
    filterRows('');
    if (modalRef.current) {
      modalRef.current.classList.add('open');
      modalRef.current.style.display = 'flex';
    }
    searchInputRef.current?.focus();
  };

  const handleMsicSelect = (msicCode: string, description: string, memberIndex: number) => {
    const member = members[memberIndex];
    const memberLabel = getMemberLabel(memberIndex);
    const memberSuffix = `_Business${memberLabel}Member`;

    lineItems.forEach((item: any) => {
      if (!item.properties?.abstract) {
        const contextRef = getBusinessContext(item, member);

        if (item.name === 'MSICCode' || item.qname === 'ssmt:MSICCode') {
          const fieldNameWithSuffix = `MSICCode${memberSuffix}`;
          onFieldChange(fieldNameWithSuffix, contextRef, msicCode, item.qname);
          devLog(`✅ Saved MSICCode: ${msicCode} to ${fieldNameWithSuffix}_${contextRef}`);
        }

        if (item.name === 'DescriptionOfBusiness' || item.qname === 'ssmt:DescriptionOfBusiness') {
          const fieldNameWithSuffix = `DescriptionOfBusiness${memberSuffix}`;
          onFieldChange(fieldNameWithSuffix, contextRef, description, item.qname);
          devLog(`✅ Saved Description: ${description} to ${fieldNameWithSuffix}_${contextRef}`);
        }
      }
    });

    document.dispatchEvent(new CustomEvent('fieldUpdated'));
    closeModal();
  };

  const buildMsicRows = () => {
    const tbody = tbodyRef.current;
    if (!tbody) return;

    tbody.replaceChildren();
    const fragment = document.createDocumentFragment();

    getMSICCodes().forEach((msicItem) => {
      const tr = document.createElement('tr');
      tr.dataset.code = msicItem.code.toLowerCase();
      tr.dataset.description = msicItem.description.toLowerCase();

      const codeTd = document.createElement('td');
      codeTd.className = 'px-4 py-2';
      codeTd.textContent = msicItem.code;

      const descTd = document.createElement('td');
      descTd.className = 'px-4 py-2';
      descTd.textContent = msicItem.description;

      const actionTd = document.createElement('td');
      actionTd.className = 'px-4 py-2';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm btn-primary';
      btn.textContent = 'Select';
      btn.dataset.msicCode = msicItem.code;
      btn.dataset.msicDescription = msicItem.description;
      actionTd.appendChild(btn);

      tr.appendChild(codeTd);
      tr.appendChild(descTd);
      tr.appendChild(actionTd);
      fragment.appendChild(tr);
    });

    tbody.appendChild(fragment);
    listBuiltRef.current = true;
    setStatus(getMSICCodes().length === 0 ? 'empty' : 'list', 'No MSIC codes found');
  };

  const loadMsicList = () => {
    if (isMSICLoaded() && listBuiltRef.current) {
      setStatus('list');
      return;
    }

    setStatus('loading', 'Loading MSIC codes...');
    loadMSICCodes()
      .then(() => {
        buildMsicRows();
      })
      .catch((err: unknown) => {
        listBuiltRef.current = false;
        setStatus(
          'error',
          err instanceof Error ? err.message : 'MSIC codes are not loaded'
        );
      });
  };

  useEffect(() => {
    loadMsicList();
  }, []);

  const handleClearMember = (memberIndex: number) => {
    const member = members[memberIndex];
    const memberLabel = getMemberLabel(memberIndex);
    const memberSuffix = `_Business${memberLabel}Member`;

    lineItems.forEach((item: any) => {
      if (!item.properties?.abstract) {
        const contextRef = getBusinessContext(item, member);
        const fieldNameWithSuffix = `${item.name}${memberSuffix}`;
        onFieldChange(fieldNameWithSuffix, contextRef, '', item.qname);
      }
    });

    document.dispatchEvent(new CustomEvent('fieldUpdated'));
  };

  const BusinessTableInput: React.FC<{
    item: any;
    contextRef: string;
    initialValue: string;
    formData: Record<string, any>;
    dataKey: string;
    onModalOpen: () => void;
    fieldNameWithSuffix: string;
    hasError: boolean;
  }> = ({ item, contextRef, initialValue, formData, dataKey, onModalOpen, fieldNameWithSuffix, hasError }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (inputRef.current) {
        const refValue = formData[dataKey] || '';
        if (inputRef.current.value !== refValue) {
          inputRef.current.value = refValue;
        }
      }
    }, [formData, dataKey]);

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          className={`form-control cursor-pointer ${hasError ? 'has-error' : ''}`}
          name={fieldNameWithSuffix}
          data-qname={item.qname}
          data-period-type={item.properties?.period_type}
          data-type={item.properties?.data_type}
          data-balance={item.properties?.balance}
          data-context-ref={contextRef}
          readOnly
          style={{
            backgroundColor: '#fff',
            opacity: 1,
            cursor: 'pointer',
            caretColor: 'transparent'
          }}
          placeholder={`Enter ${item.standard_label}`}
          defaultValue={initialValue}
          onClick={onModalOpen}
          onChange={() => {}}
          onFocus={(e) => e.target.blur()}
        />
        <ValidationIndicator fieldName={fieldNameWithSuffix} contextRef={contextRef} />
      </div>
    );
  };

  return (
    <div className="business-table-section">
      <h3 className="section-header">{table?.standard_label}</h3>

      <div className="card-table">
        <table className="table-element" data-datatable-table="true">
          <thead>
            <tr>
              <th></th>
              {members.map((member: any, memberIndex: number) => (
                <th key={member.name}>
                  <span className="sort" style={{ display: 'flex', width: '100%' }}>
                    <span className="sort-label">{member.standard_label}</span>
                    <span
                      title="Clear this column"
                      onClick={() => handleClearMember(memberIndex)}
                      style={{ cursor: 'pointer' }}
                    >
                      <svg
                        className="w-5 h-5 hover:text-red-500 transition"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="form-element">
            {lineItems.map((item: any) => {
              if (item.properties?.abstract) return null;

              return (
                <tr key={item.name}>
                  <td className="table-title-0 c-black">{item.standard_label}</td>
                  {members.map((member: any, memberIndex: number) => {
                    const contextRef = getBusinessContext(item, member);
                    const memberLabel = getMemberLabel(memberIndex);
                    const memberSuffix = `_Business${memberLabel}Member`;
                    const fieldNameWithSuffix = `${item.name}${memberSuffix}`;
                    const dataKey = `${item.qname}_${contextRef}`;
                    const initialValue = formData[dataKey] || '';
                    const fieldHasError = hasFieldError ? hasFieldError(fieldNameWithSuffix, contextRef) : false;

                    return (
                      <td key={`${item.name}_${member.name}`} className="table-input">
                        <BusinessTableInput
                          item={item}
                          contextRef={contextRef}
                          initialValue={initialValue}
                          formData={formData}
                          dataKey={dataKey}
                          onModalOpen={() => openModal(memberIndex)}
                          fieldNameWithSuffix={fieldNameWithSuffix}
                          hasError={fieldHasError}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        ref={modalRef}
        className="modal"
        style={{ display: 'none' }}
      >
        <div className="modal-backdrop" onClick={closeModal}></div>
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title">Search MSIC Code</h3>
            <button type="button" onClick={closeModal}>
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="modal-searchbar form-element">
              <input
                ref={searchInputRef}
                type="text"
                className="form-control msic-search-input"
                placeholder="Search by MSIC code or description..."
                defaultValue=""
                data-skip-validation="true"
                onInput={(e) => filterRows((e.target as HTMLInputElement).value)}
              />
              <button
                type="button"
                className="btn btn-search msic-search-btn"
                onClick={() => filterRows(searchInputRef.current?.value || '')}
              >
                Search
              </button>
              <button
                type="button"
                className="btn btn-reset msic-reset-btn"
                onClick={() => {
                  if (searchInputRef.current) searchInputRef.current.value = '';
                  filterRows('');
                }}
              >
                Reset
              </button>
            </div>
            <div ref={statusRef} className="text-center py-8">
              <p className="text-sm text-gray-600">Loading MSIC codes...</p>
              <button
                ref={retryBtnRef}
                type="button"
                className="btn btn-sm btn-primary"
                style={{ display: 'none', marginTop: '12px' }}
                onClick={loadMsicList}
              >
                Retry
              </button>
            </div>
            <div
              ref={tableWrapRef}
              className="overflow-x-auto"
              style={{ maxHeight: '400px', overflowY: 'auto', display: 'none' }}
            >
              <table className="table-element w-full">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th className="px-4 py-2">MSIC</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody
                  ref={tbodyRef}
                  className="msic-table-body"
                  onClick={(event) => {
                    const btn = (event.target as HTMLElement).closest('button[data-msic-code]') as HTMLButtonElement | null;
                    if (!btn) return;
                    handleMsicSelect(
                      btn.dataset.msicCode || '',
                      btn.dataset.msicDescription || '',
                      activeMemberIndexRef.current
                    );
                  }}
                />
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeModal}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const BusinessTable = React.memo(BusinessTableComponent);
